import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './AdminDashboard.css';

const TechnicianLocations = () => {
  const [technicians, setTechnicians] = useState([]);
  const [selectedTechnician, setSelectedTechnician] = useState(null);
  const [locationHistory, setLocationHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [mapLoaded, setMapLoaded] = useState(false);

  useEffect(() => {
    fetchTechnicians();
    loadGoogleMapsScript();
  }, []);

  const loadGoogleMapsScript = () => {
    if (window.google) {
      setMapLoaded(true);
      return;
    }

    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=AIzaSyAbs-7oeN08sYj4kwVSV-9OvKs13gp4URs&libraries=places`;
    script.async = true;
    script.defer = true;
    script.onload = () => setMapLoaded(true);
    script.onerror = () => setError('Failed to load Google Maps');
    document.head.appendChild(script);
  };

  const fetchTechnicians = async () => {
    try {
      const response = await axios.get('https://madhusewingmachines.com/api/technicians');
      const techniciansWithLocation = response.data.data.filter(tech => 
        tech.currentLocation && tech.currentLocation.latitude && tech.currentLocation.longitude
      );
      setTechnicians(techniciansWithLocation);
      setLoading(false);
    } catch (error) {
      console.error('Error fetching technicians:', error);
      setError('Failed to fetch technicians');
      setLoading(false);
    }
  };

  const fetchLocationHistory = async (technicianId) => {
    try {
      const response = await axios.get(
        `https://madhusewingmachines.com/api/technicians/${technicianId}/location/history?limit=20`
      );
      setLocationHistory(response.data.data.locationHistory);
    } catch (error) {
      console.error('Error fetching location history:', error);
      setError('Failed to fetch location history');
    }
  };

  const handleTechnicianSelect = (technician) => {
    setSelectedTechnician(technician);
    fetchLocationHistory(technician._id);
  };

  const initializeMap = (technician) => {
    if (!mapLoaded || !window.google) return;

    const mapElement = document.getElementById('map');
    if (!mapElement) return;

    const map = new window.google.maps.Map(mapElement, {
      center: {
        lat: technician.currentLocation.latitude,
        lng: technician.currentLocation.longitude
      },
      zoom: 15,
      mapTypeId: 'roadmap'
    });

    // Add marker for current location
    new window.google.maps.Marker({
      position: {
        lat: technician.currentLocation.latitude,
        lng: technician.currentLocation.longitude
      },
      map: map,
      title: `${technician.name} - Current Location`,
      icon: {
        url: 'https://maps.google.com/mapfiles/ms/icons/red-dot.png',
        scaledSize: new window.google.maps.Size(32, 32)
      }
    });

    // Add info window
    const infoWindow = new window.google.maps.InfoWindow({
      content: `
        <div style="padding: 10px;">
          <h4>${technician.name}</h4>
          <p><strong>Current Location:</strong></p>
          <p>${technician.currentLocation.address || 'Address not available'}</p>
          <p><strong>Last Updated:</strong></p>
          <p>${new Date(technician.currentLocation.lastUpdated).toLocaleString()}</p>
        </div>
      `
    });

    // Show info window on marker click
    const marker = new window.google.maps.Marker({
      position: {
        lat: technician.currentLocation.latitude,
        lng: technician.currentLocation.longitude
      },
      map: map,
      title: technician.name
    });

    marker.addListener('click', () => {
      infoWindow.open(map, marker);
    });
  };

  useEffect(() => {
    if (selectedTechnician && mapLoaded) {
      setTimeout(() => initializeMap(selectedTechnician), 100);
    }
  }, [selectedTechnician, mapLoaded]);

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const getTimeDifference = (dateString) => {
    const now = new Date();
    const then = new Date(dateString);
    const diffInMinutes = Math.floor((now - then) / (1000 * 60));
    
    if (diffInMinutes < 1) return 'Just now';
    if (diffInMinutes < 60) return `${diffInMinutes} minutes ago`;
    if (diffInMinutes < 1440) return `${Math.floor(diffInMinutes / 60)} hours ago`;
    return `${Math.floor(diffInMinutes / 1440)} days ago`;
  };

  if (loading) {
    return (
      <div className="admin-container">
        <div className="loading-spinner">Loading technician locations...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-container">
        <div className="error-message">{error}</div>
      </div>
    );
  }

  return (
    <div className="admin-container">
      <div className="admin-header">
        <h1>Technician Locations</h1>
        <p>Track real-time locations of active technicians</p>
      </div>

      <div className="locations-layout">
        {/* Technicians List */}
        <div className="technicians-sidebar">
          <h3>Active Technicians ({technicians.length})</h3>
          <div className="technicians-list">
            {technicians.map((technician) => (
              <div
                key={technician._id}
                className={`technician-card ${selectedTechnician?._id === technician._id ? 'selected' : ''}`}
                onClick={() => handleTechnicianSelect(technician)}
              >
                <div className="technician-info">
                  <h4>{technician.name}</h4>
                  <p className="technician-phone">{technician.phone}</p>
                  <p className="location-time">
                    Last seen: {getTimeDifference(technician.currentLocation.lastUpdated)}
                  </p>
                  <div className="location-status">
                    <span className="status-dot active"></span>
                    <span>Active</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Map and Details */}
        <div className="location-details">
          {selectedTechnician ? (
            <>
              <div className="selected-technician-header">
                <h3>{selectedTechnician.name}</h3>
                <div className="technician-meta">
                  <span>Phone: {selectedTechnician.phone}</span>
                  <span>Email: {selectedTechnician.email}</span>
                  <span>Experience: {selectedTechnician.experience} years</span>
                </div>
              </div>

              {/* Map Container */}
              <div className="map-container">
                <div id="map" style={{ width: '100%', height: '400px' }}></div>
              </div>

              {/* Current Location Info */}
              <div className="current-location-info">
                <h4>Current Location</h4>
                <div className="location-details-grid">
                  <div className="location-item">
                    <strong>Address:</strong>
                    <span>{selectedTechnician.currentLocation.address || 'Address not available'}</span>
                  </div>
                  <div className="location-item">
                    <strong>Coordinates:</strong>
                    <span>
                      {selectedTechnician.currentLocation.latitude.toFixed(6)}, 
                      {selectedTechnician.currentLocation.longitude.toFixed(6)}
                    </span>
                  </div>
                  <div className="location-item">
                    <strong>Last Updated:</strong>
                    <span>{formatDateTime(selectedTechnician.currentLocation.lastUpdated)}</span>
                  </div>
                </div>
              </div>

              {/* Location History */}
              <div className="location-history">
                <h4>Recent Location History</h4>
                <div className="history-list">
                  {locationHistory.map((location, index) => (
                    <div key={index} className="history-item">
                      <div className="history-time">
                        {formatDateTime(location.timestamp)}
                      </div>
                      <div className="history-location">
                        <strong>Address:</strong> {location.address || 'Address not available'}
                      </div>
                      <div className="history-coordinates">
                        <strong>Coordinates:</strong> {location.latitude.toFixed(6)}, {location.longitude.toFixed(6)}
                      </div>
                      {location.jobId && (
                        <div className="history-job">
                          <strong>Job ID:</strong> {location.jobId}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </>
          ) : (
            <div className="no-selection">
              <h3>Select a technician to view their location</h3>
              <p>Choose a technician from the list to see their current location and tracking history.</p>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .locations-layout {
          display: flex;
          gap: 20px;
          margin-top: 20px;
        }

        .technicians-sidebar {
          width: 300px;
          background: white;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
          height: fit-content;
        }

        .technicians-list {
          max-height: 600px;
          overflow-y: auto;
        }

        .technician-card {
          border: 1px solid #e0e0e0;
          border-radius: 8px;
          padding: 15px;
          margin-bottom: 10px;
          cursor: pointer;
          transition: all 0.3s ease;
        }

        .technician-card:hover {
          border-color: #007bff;
          box-shadow: 0 2px 8px rgba(0,123,255,0.1);
        }

        .technician-card.selected {
          border-color: #007bff;
          background-color: #f8f9ff;
        }

        .technician-info h4 {
          margin: 0 0 5px 0;
          color: #333;
        }

        .technician-phone {
          color: #666;
          font-size: 14px;
          margin: 0 0 5px 0;
        }

        .location-time {
          color: #888;
          font-size: 12px;
          margin: 0 0 10px 0;
        }

        .location-status {
          display: flex;
          align-items: center;
          gap: 5px;
          font-size: 12px;
          color: #28a745;
        }

        .status-dot {
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #28a745;
        }

        .location-details {
          flex: 1;
          background: white;
          border-radius: 8px;
          padding: 20px;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .selected-technician-header {
          margin-bottom: 20px;
          padding-bottom: 15px;
          border-bottom: 1px solid #e0e0e0;
        }

        .selected-technician-header h3 {
          margin: 0 0 10px 0;
          color: #333;
        }

        .technician-meta {
          display: flex;
          gap: 20px;
          font-size: 14px;
          color: #666;
        }

        .map-container {
          margin-bottom: 20px;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }

        .current-location-info {
          margin-bottom: 20px;
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .current-location-info h4 {
          margin: 0 0 15px 0;
          color: #333;
        }

        .location-details-grid {
          display: grid;
          gap: 10px;
        }

        .location-item {
          display: flex;
          justify-content: space-between;
          padding: 5px 0;
        }

        .location-item strong {
          color: #333;
          min-width: 120px;
        }

        .location-history {
          padding: 15px;
          background: #f8f9fa;
          border-radius: 8px;
        }

        .location-history h4 {
          margin: 0 0 15px 0;
          color: #333;
        }

        .history-list {
          max-height: 300px;
          overflow-y: auto;
        }

        .history-item {
          background: white;
          border-radius: 6px;
          padding: 12px;
          margin-bottom: 10px;
          border-left: 3px solid #007bff;
        }

        .history-time {
          font-weight: bold;
          color: #007bff;
          margin-bottom: 5px;
        }

        .history-location,
        .history-coordinates,
        .history-job {
          font-size: 14px;
          color: #666;
          margin-bottom: 3px;
        }

        .no-selection {
          text-align: center;
          padding: 60px 20px;
          color: #666;
        }

        .no-selection h3 {
          margin-bottom: 10px;
          color: #333;
        }

        .loading-spinner {
          text-align: center;
          padding: 40px;
          font-size: 18px;
          color: #666;
        }

        .error-message {
          text-align: center;
          padding: 40px;
          color: #dc3545;
          background: #f8d7da;
          border-radius: 8px;
          margin: 20px;
        }
      `}</style>
    </div>
  );
};

export default TechnicianLocations;